import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import react, { useEffect, useState } from 'react';
import OrderTransformation from './OrderTransformation';

function TransformationModal({
  isModalOpen,
  handleCloseModal,
  data: { formField, setFormField, sourceColumns, targetColumns, reactFlowMapping },
  label,
}) {
  const [transformation, setTransformation] = useState({
    type: '',
    params: {},
  });
  const [orderData, setOrderData] = useState('');
  const destDatatype = targetColumns.find((col) => col.header === label)?.cast_datatype || null;

  const resetTransformation = () => setTransformation({ type: '', params: {} });

  const handleClose = () => {
    resetTransformation(); // Reset the transformation state when closing the modal
    handleCloseModal(); // Close the modal
  };

  useEffect(() => {
    if (isModalOpen) {
      const mapping = formField.mappings.find((mapping) => mapping.destination_column.name === label);
      setTransformation(mapping?.transformation || { type: '', params: {} });

      const matchingSources = reactFlowMapping
        .filter((item) => item.destination.name === label)
        .map((item) => item.source.name);
      setOrderData(matchingSources);
    }
  }, [isModalOpen, formField.mappings, label]);

  const handleSave = () => {
    // Check if transformation type is 'Concatenation'
    const isConcatenation = transformation?.type === 'Concatenation';

    // Prepare the new transformation based on prefix and postfix values
    const newTransformation = {
      ...transformation,
      params: {
        ...transformation.params,
        prefix: transformation.params.prefix || null, // Set to null if prefix is an empty string
        postfix: transformation.params.postfix || null, // Set to null if postfix is an empty string
      },
    };

    // Update the mappings based on whether it's a concatenation or not
    const updatedMappings = formField.mappings.map((mapping) => {
      const isMatchingDestination = mapping.destination_column?.name === label;

      return isMatchingDestination
        ? { ...mapping, transformation: isConcatenation ? newTransformation : transformation }
        : mapping;
    });

    // Update the formField state
    setFormField({ ...formField, mappings: updatedMappings });
    handleClose();
  };

  const handleFieldChange = (field) => (event) => {
    if (field === 'inputFormatConv') {
      setTransformation((prev) => ({
        ...prev,
        params: { ...prev.params, [field]: event.target.value, outputFormatDT: destDatatype },
      }));
      return;
    }
    setTransformation((prev) => ({
      ...prev,
      params: { ...prev.params, [field]: event.target.value },
    }));
  };

  const handleOperationChange = (event) => {
    const value = event.target.value;
    setTransformation({
      type: value,
      params: value === 'Concatenation' ? { prefix: '', postfix: '', order: orderData } : {},
    });
  };

  const renderTextField = (field, options) => (
    <TextField
      key={field.id}
      id={field.id}
      label={field.label || field.label2}
      select={!!options}
      size="small"
      value={field.type === 'prefetchValue' ? destDatatype : transformation.params[field.id] || ''}
      onChange={handleFieldChange(field.id)}
      autoComplete="off"
      disabled={field.type === 'prefetchValue'}
      style={{ width: '100%', marginTop: '10px' }}
    >
      {options &&
        options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
    </TextField>
  );

  const operationFields = {
    Concatenation: [
      { id: 'separator', label: 'Separator' },
      { id: 'prefix', label: 'Prefix' },
      { id: 'postfix', label: 'Postfix' },
      { id: 'order', label: 'Order', type: 'dragAndDrop' },
    ],
    ArithmeticOperation: [
      { id: 'parameters', label2: 'Parameters', type: 'dropdown', options: ['addition', 'subtraction'] },
    ],
    Substring: [
      { id: 'startIndex', label: 'Start Index' },
      { id: 'length', label: 'Length' },
    ],
    DateTimeFormatting: [
      { id: 'inputFormatDT', label: 'Input Format', type: 'DateTimeFormat' },
      { id: 'outputFormatDT', label: 'Output Format', type: 'DateTimeFormat' },
    ],
    ConditionalFormatting: [
      { id: 'condition', label: 'Condition', type: 'SplitConditionInput' },
      { id: 'trueVal', label: 'True Value' },
      { id: 'falseVal', label: 'False Value' },
    ],
    NullHandling: [{ id: 'value', label: 'Replace Null With Default Value' }],
    DataTypeConversion: [
      { id: 'inputFormatConv', label: 'Input Format', type: 'dataTypeFormat' },
      { id: 'outputFormatConv', label: 'Output Format', type: 'prefetchValue' },
    ],
  };

  const dateFormats = [
    'yyyy-MM-dd',
    'MM/dd/yyyy',
    'dd/MM/yyyy',
    'yyyy/MM/dd',
    'yyyy-MM-dd HH:mm:ss',
    'MM/dd/yyyy HH:mm:ss',
    "yyyy-MM-dd'T'HH:mm:ss",
    'yyyy/MM/dd HH:mm:ss',
    "yyyy-MM-dd'T'HH:mm:ssXXX",
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
    'yyyy-MM-dd HH:mm:ss z',
    'yy-MM-dd',
    'dd/MM/yy',
    'MM/dd/yy',
  ];

  const renderConditionInput = () => (
    <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
      <TextField
        id="column_name"
        name="Column name"
        label="Column name"
        select
        size="small"
        value={transformation.params.column_name || ''}
        onChange={handleFieldChange('column_name')}
        autoComplete="off"
        style={{ flex: 1 }}
      >
        {sourceColumns &&
          sourceColumns.map((item) => (
            <MenuItem key={item.header} value={item.header}>
              {item.header}
            </MenuItem>
          ))}
      </TextField>

      <TextField
        id="logic"
        name="Logic"
        label="Logic"
        select
        size="small"
        value={transformation.params.logic || ''}
        onChange={handleFieldChange('logic')}
        autoComplete="off"
        style={{ flex: 1 }}
      >
        <MenuItem value="<">Smaller than</MenuItem>
        <MenuItem value=">">Greater than</MenuItem>
        <MenuItem value="==">Equal to</MenuItem>
        <MenuItem value="!=">Not equal to</MenuItem>
        <MenuItem value="<=">Smaller than or equal to</MenuItem>
        <MenuItem value=">=">Greater than or equal to</MenuItem>
      </TextField>

      <TextField
        id="value"
        name="Value"
        label="Value"
        size="small"
        value={transformation.params.value || ''}
        onChange={handleFieldChange('value')}
        autoComplete="off"
        style={{ flex: 1 }}
      />
    </div>
  );

  const renderDragAndDrop = (field, options) => (
    <OrderTransformation orderData={orderData} setOrderData={setOrderData} setTransformation={setTransformation} />
  );

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleClose}
      PaperProps={{
        style: { minHeight: '400px', maxWidth: '500px' },
      }}
    >
      <DialogTitle>Transformation</DialogTitle>
      <DialogContent>
        <div style={{ width: 400, marginTop: '2px' }}>
          <TextField
            id="outlined-select-operation"
            select
            required
            name="operation"
            label="Operation"
            size="small"
            value={transformation.type || ''}
            onChange={handleOperationChange}
            style={{ width: '100%', marginTop: '5px' }}
          >
            {Object.keys(operationFields).map((op) => (
              <MenuItem key={op} value={op}>
                {op.replace(/([A-Z])/g, ' $1').trim()}
              </MenuItem>
            ))}
          </TextField>

          {operationFields[transformation.type]?.map((field) => {
            if (field.type === 'dropdown') {
              return renderTextField(field, field.options);
            }
            if (field.type === 'DateTimeFormat') {
              return renderTextField(field, dateFormats);
            }
            if (field.type === 'dataTypeFormat') {
              return renderTextField(field, ['Number', 'Float', 'String']);
            }
            if (field.type === 'SplitConditionInput') {
              return renderConditionInput(field);
            }
            if (field.type === 'dragAndDrop') {
              return renderDragAndDrop(field);
            }
            return renderTextField(field);
          })}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
        <Button onClick={handleSave} color="primary">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TransformationModal;
